import { useEffect, useState } from 'react'

import { ToastService } from '../../../services/toast.service'
import { fetchSiteTreeView, SiteAsset, SiteTreeViewResponse } from '../../copSiteDetails/services/copSiteDetails.service'

export type FilterType = 'all' | 'spare' | 'decom'

function collectAllAssetIds(assets: SiteAsset[]): string[] {
  return assets.flatMap(a => [a.assetId, ...collectAllAssetIds(a.assets ?? [])])
}

function toggleSet<T>(prev: Set<T>, item: T): Set<T> {
  const next = new Set(prev)
  if (next.has(item)) { next.delete(item) } else { next.add(item) }
  return next
}

export function useAssetsTree(isOpen: boolean, siteId: string, errorMessageLabel?: string) {
  const [treeData, setTreeData] = useState<SiteTreeViewResponse | null>(null)
  const [hasApiError, setHasApiError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isSiteExpanded, setIsSiteExpanded] = useState(true)
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set())
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set())
  const [isSpareGroupExpanded, setIsSpareGroupExpanded] = useState(false)
  const [isDecomGroupExpanded, setIsDecomGroupExpanded] = useState(false)

  useEffect(() => {
    if (!isOpen || !siteId) return
    let cancelled = false
    setIsLoading(true)
    setHasApiError(false)
    fetchSiteTreeView(siteId)
      .then(data => {
        if (cancelled) return
        if (data) {
          setTreeData(data)
          setExpandedAreas(new Set())
          setExpandedAssets(new Set())
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setHasApiError(true)
        const message = error instanceof Error ? error.message : ''
        ToastService.error(message || errorMessageLabel || '')
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, siteId, errorMessageLabel])

  const handleFilterSelect = (filter: FilterType) => {
    setSelectedFilter(filter)
    setIsDropdownOpen(false)
  }

  const toggleArea = (key: string) => setExpandedAreas(prev => toggleSet(prev, key))
  const toggleAsset = (id: string) => setExpandedAssets(prev => toggleSet(prev, id))

  const handleExpandAll = () => {
    if (!treeData) return
    const areaKeys = new Set([
      ...(treeData.siteAreas ?? []).map((_, i) => `area-${i}`),
      ...(treeData.spareAssetsContainers ?? []).map((_, i) => `spare-${i}`),
      ...(treeData.decommissionedAssetsContainers ?? []).map((_, i) => `decom-${i}`),
    ])
    const allAssetIds = new Set([
      ...collectAllAssetIds(treeData.assets ?? []),
      ...collectAllAssetIds((treeData.siteAreas ?? []).flatMap(a => a.assets)),
      ...collectAllAssetIds((treeData.spareAssetsContainers ?? []).flatMap(a => a.assets)),
      ...collectAllAssetIds((treeData.decommissionedAssetsContainers ?? []).flatMap(a => a.assets)),
    ])
    setIsSiteExpanded(true)
    setExpandedAreas(areaKeys)
    setExpandedAssets(allAssetIds)
    setIsSpareGroupExpanded(true)
    setIsDecomGroupExpanded(true)
  }

  const handleCollapseAll = () => {
    setIsSiteExpanded(false)
    setExpandedAreas(new Set())
    setExpandedAssets(new Set())
    setIsSpareGroupExpanded(false)
    setIsDecomGroupExpanded(false)
  }

  return {
    treeData, hasApiError, isLoading,
    selectedFilter, isDropdownOpen, setIsDropdownOpen,
    isSiteExpanded, setIsSiteExpanded,
    expandedAreas, expandedAssets,
    isSpareGroupExpanded, setIsSpareGroupExpanded,
    isDecomGroupExpanded, setIsDecomGroupExpanded,
    handleFilterSelect, toggleArea, toggleAsset,
    handleExpandAll, handleCollapseAll,
  }
}
